import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Trash2, RotateCcw, Clock, FolderOpen, CheckSquare, AlertTriangle } from 'lucide-react';

const Trash = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [deletedProjects, setDeletedProjects] = useState([]);
  const [deletedTasks, setDeletedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('projects');

  useEffect(() => {
    fetchTrashItems();
  }, []);

  const fetchTrashItems = async () => {
    try {
      const [projectsRes, tasksRes] = await Promise.all([
        axios.get(`${API}/trash/projects`),
        axios.get(`${API}/trash/tasks`)
      ]);
      setDeletedProjects(projectsRes.data);
      setDeletedTasks(tasksRes.data);
    } catch (error) {
      console.error('Error fetching trash:', error);
      toast.error('Fehler beim Laden des Papierkorbs');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreProject = async (projectId) => {
    try {
      await axios.post(`${API}/trash/projects/${projectId}/restore`);
      toast.success('Projekt wiederhergestellt');
      fetchTrashItems();
    } catch (error) {
      toast.error('Fehler beim Wiederherstellen');
    }
  };

  const handleRestoreTask = async (taskId) => {
    try {
      await axios.post(`${API}/trash/tasks/${taskId}/restore`);
      toast.success('Task wiederhergestellt');
      fetchTrashItems();
    } catch (error) {
      toast.error('Fehler beim Wiederherstellen');
    }
  };

  const handlePermanentDeleteProject = async (projectId, projectName) => {
    if (!window.confirm(`Projekt "${projectName}" endgültig löschen? Dies kann nicht rückgängig gemacht werden!`)) {
      return;
    }
    try {
      await axios.delete(`${API}/trash/projects/${projectId}/permanent`);
      toast.success('Projekt endgültig gelöscht');
      fetchTrashItems();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handlePermanentDeleteTask = async (taskId, taskTitle) => {
    if (!window.confirm(`Task "${taskTitle}" endgültig löschen? Dies kann nicht rückgängig gemacht werden!`)) {
      return;
    }
    try {
      await axios.delete(`${API}/trash/tasks/${taskId}/permanent`);
      toast.success('Task endgültig gelöscht');
      fetchTrashItems();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysRemainingBadge = (daysRemaining) => {
    if (daysRemaining <= 3) {
      return <Badge variant="destructive" className="ml-2"><Clock className="w-3 h-3 mr-1" />{daysRemaining} Tage</Badge>;
    } else if (daysRemaining <= 7) {
      return <Badge className="ml-2 bg-orange-500"><Clock className="w-3 h-3 mr-1" />{daysRemaining} Tage</Badge>;
    }
    return <Badge variant="secondary" className="ml-2"><Clock className="w-3 h-3 mr-1" />{daysRemaining} Tage</Badge>;
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Trash2 className="w-8 h-8 text-muted-foreground" />
              Papierkorb
            </h1>
            <p className="text-muted-foreground mt-1">
              Gelöschte Projekte und Tasks werden nach 30 Tagen automatisch entfernt
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <p className="text-sm text-amber-800 dark:text-amber-400">
              Elemente im Papierkorb können innerhalb von 30 Tagen wiederhergestellt werden. 
              Danach werden sie automatisch und unwiderruflich gelöscht.
            </p>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="projects" className="flex items-center gap-2">
              <FolderOpen className="w-4 h-4" />
              Projekte
              {deletedProjects.length > 0 && (
                <Badge variant="secondary" className="ml-1">{deletedProjects.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="tasks" className="flex items-center gap-2">
              <CheckSquare className="w-4 h-4" />
              Tasks
              {deletedTasks.length > 0 && (
                <Badge variant="secondary" className="ml-1">{deletedTasks.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Projects Tab */}
          <TabsContent value="projects" className="mt-4">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Laden...</div>
            ) : deletedProjects.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Trash2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Keine gelöschten Projekte</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {deletedProjects.map((project) => (
                  <Card key={project.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg">{project.name}</h3>
                            {getDaysRemainingBadge(project.days_remaining)}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{project.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span>Gelöscht: {formatDate(project.deleted_at)}</span>
                            <span>Zeitraum: {project.start_date} - {project.end_date}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestoreProject(project.id)}
                            className="text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700"
                          >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Wiederherstellen
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePermanentDeleteProject(project.id, project.name)}
                            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Endgültig löschen
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks" className="mt-4">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Laden...</div>
            ) : deletedTasks.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Trash2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Keine gelöschten Tasks</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {deletedTasks.map((task) => (
                  <Card key={task.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{task.title}</h3>
                            {task.priority && (
                              <Badge className={`text-xs ${
                                task.priority === 'high' 
                                  ? 'bg-red-500' 
                                  : task.priority === 'medium' 
                                    ? 'bg-yellow-500 text-yellow-950' 
                                    : 'bg-green-500'
                              }`}>
                                {task.priority === 'high' ? 'Hoch' : task.priority === 'medium' ? 'Mittel' : 'Niedrig'}
                              </Badge>
                            )}
                            {getDaysRemainingBadge(task.days_remaining)}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{task.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="font-medium">Projekt: {task.project_name}</span>
                            <span>Gelöscht: {formatDate(task.deleted_at)}</span>
                            <span>Fällig: {task.due_date}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestoreTask(task.id)}
                            className="text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700"
                          >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Wiederherstellen
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePermanentDeleteTask(task.id, task.title)}
                            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Endgültig löschen
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Trash;
